"use client";

import ListCard from "@/components/views/list/card/VenueCard";
import apiFetch from "@/lib/fetch";
import { useEffect, useState, useRef, useMemo } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import { VenueLoading } from "@/components/views/list/venue-list/Venue";
import { DEFAULT_CACHE_DURATION } from "@/@core/constants/cache";
import { useUserLocation } from "@/context/UserLocationContext";

// Desktop homepage cards show one photo rather than the 4-photo carousel the
// listing pages use. Raise this to bring the carousel back.
const HOMEPAGE_VENUE_CARD_PHOTO_LIMIT = 1;

export default function FeaturedVenuesClient({ data }: { data: any }) {
  const [isLoading, setIsLoading] = useState(true);
  const [featuredVenues, setFeaturedVenues] = useState<any[]>(
    data ? data : [],
  );
  const [hasMore, setHasMore] = useState(true);

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
    sortByDistance,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (data.length < DEFAULT_DATA_LIMIT) {
      setHasMore(false);
    }
    setIsLoading(false);
  }, [data]);

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && featuredVenues.length > 0) {
      const venuesToCalculate = featuredVenues
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, featuredVenues, calculateDistances]);

  const fetchData = async (index = 0) => {
    try {
      setIsLoading(true);
      const res = await apiFetch({
        route: `venue/featured?index=${index}`,
        revalidate: DEFAULT_CACHE_DURATION, // Cache for 1 hour
      });
      
      if (res.length < DEFAULT_DATA_LIMIT) {
        setHasMore(false);
      }
      
      return res;
    } catch (error) {
      console.error('Error fetching featured venues:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const next = (page: number) => {
    fetchData(page).then((data) => {
      setFeaturedVenues([...featuredVenues, ...data]);
    });
  };

  // When the user activates "Yakındakiler", order the loaded venues by distance
  // (ascending). Venues without a calculated distance are placed at the end.
  const sortedVenues = useMemo(() => {
    if (!sortByDistance || venueDistances.size === 0) return featuredVenues;

    return [...featuredVenues].sort((a, b) => {
      const da = venueDistances.get(a.id);
      const db = venueDistances.get(b.id);
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  }, [featuredVenues, sortByDistance, venueDistances]);

  if (!Array.isArray(featuredVenues) || !featuredVenues) return <></>;

  return (
    <InfiniteScroll
      hasMore={hasMore}
      next={next}
      loading={<VenueLoading count={6} />}
      isLoading={isLoading}
      skeletonElementId="venue-card-skeleton"
      dataLength={featuredVenues?.length || 0}
    >
      <div className="w-full xl:grid flex flex-wrap justify-center xl:grid-cols-3 gap-5">
        {Array.isArray(sortedVenues) &&
          sortedVenues?.map((venue: any, index: number) => {
            const distance = venueDistances.get(venue.id);
            return (
              <ListCard
                titleHeadingLevel="h3"
                key={index}
                distance={distance}
                {...venue}
                index={index}
                // SAB-345: the homepage renders a single photo per card instead
                // of the 4-photo carousel, to keep the DOM (and the number of
                // image requests) down on the heaviest page of the site.
                photoLimit={HOMEPAGE_VENUE_CARD_PHOTO_LIMIT}
              />
            );
          })}
      </div>
    </InfiniteScroll>
  );
}
